import React, { useCallback, useEffect, useState } from 'react';
import { Button, SideBarModal, SignaturePad } from '@/components';
import { RentalDetail } from '@/types/rental';
import { CreateHandoverBody } from '@/services/api/opsHandovers';
import { HandoverChecklistItemInput } from '@/services/api/handovers';
import HandoverPartChecklist from '@/pages/handovers/components/AddHandoverModal/HandoverPartChecklist';

interface FormErrors {
  odometer?: string;
  checklist?: string;
  driverSignature?: string;
  opsSignature?: string;
}

interface CreateHandoverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (body: Omit<CreateHandoverBody, 'handoverID'>) => void;
  isLoading?: boolean;
  detail: RentalDetail;
}

const inputClass = (hasError?: boolean) =>
  `w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none focus:ring-1 focus:ring-primary ${
    hasError ? 'border-red-500' : 'border-gray-200'
  }`;

/**
 * Records the physical pickup, which moves POSSESSION to ACTIVE. The rental
 * itself has been ACTIVE since the DP landed; what this unlocks is billing.
 *
 * Both signatures are collected here and are mandatory: the ops endpoint
 * validates and persists them (B6), so a submit without them is a 400. Per TRD
 * O8 the two parties sign on the one device running this screen — the mitra
 * signs first, then ops — which is why they sit side by side rather than being
 * gathered separately.
 */
export const CreateHandoverModal: React.FC<CreateHandoverModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  detail,
}) => {
  const [odometer, setOdometer] = useState('');
  const [notes, setNotes] = useState('');
  const [checklistItems, setChecklistItems] = useState<
    HandoverChecklistItemInput[]
  >([]);
  const [checklistComplete, setChecklistComplete] = useState(false);
  const [driverSignature, setDriverSignature] = useState('');
  const [opsSignature, setOpsSignature] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (!isOpen) return;
    setOdometer('');
    setNotes('');
    setChecklistItems([]);
    setChecklistComplete(false);
    setDriverSignature('');
    setOpsSignature('');
    setErrors({});
  }, [isOpen]);

  const dpPaid = detail.dpPaid;

  const handleChecklistChange = useCallback(
    (items: HandoverChecklistItemInput[], complete: boolean) => {
      setChecklistItems(items);
      setChecklistComplete(complete);
      if (complete) setErrors((prev) => ({ ...prev, checklist: undefined }));
    },
    []
  );

  const handleSubmit = () => {
    // All are rejected server-side when absent, so they are checked here rather
    // than spending a round trip on a 400.
    const nextErrors: FormErrors = {};
    if (!odometer.trim()) nextErrors.odometer = 'Odometer wajib diisi';
    if (!checklistComplete) {
      nextErrors.checklist =
        'Lengkapi checklist: tandai tiap kelengkapan dan foto yang diperlukan.';
    }
    if (!driverSignature) {
      nextErrors.driverSignature = 'Tanda tangan mitra wajib diisi';
    }
    if (!opsSignature) nextErrors.opsSignature = 'Tanda tangan ops wajib diisi';

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    onSubmit({
      // Integer, never a string — the API validates the type (B6).
      odometer: Number(odometer),
      checklistItems,
      signatures: { driver: driverSignature, ops: opsSignature },
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
  };

  if (!isOpen) return null;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="center"
      width="full"
      header={
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Handover Unit</h2>
          <p className="text-sm font-normal text-gray-600 mt-1">
            Catat serah terima unit. Sewa akan aktif setelah handover selesai.
          </p>
        </div>
      }
      footer={
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isLoading || !dpPaid}
          className="w-full py-3"
        >
          {isLoading ? 'Memproses...' : 'Selesaikan Handover'}
        </Button>
      }
    >
      <div className="flex-1 space-y-4 px-6 py-4">
        {/* The backend hard-gates on the DP being PAID (TRD B3), so surface it
            here rather than letting the submit fail. */}
        {!dpPaid && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
            <p className="text-[13px] text-amber-700">
              Uang muka belum dibayar, sehingga handover belum bisa dilakukan.
            </p>
          </div>
        )}

        <div className="rounded-lg bg-gray-50 px-3 py-2.5 space-y-1">
          <p className="text-[13px] text-gray-500">
            Mitra:{' '}
            <span className="text-gray-900">
              {detail.driver?.name || '-'} ({detail.driver?.code || '-'})
            </span>
          </p>
          <p className="text-[13px] text-gray-500">
            Unit:{' '}
            <span className="text-gray-900">
              {detail.vehicle?.platNomor || 'Belum ditugaskan'}
            </span>
          </p>
          <p className="text-[13px] text-gray-500">
            Paket:{' '}
            <span className="text-gray-900">
              {detail.package?.name || '-'}
            </span>
          </p>
        </div>

        <div>
          <label className="block text-[14px] text-gray-900 mb-1.5">
            Odometer (Km) <span className="text-primary">*</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={odometer}
            onChange={(e) => setOdometer(e.target.value.replace(/\D/g, ''))}
            placeholder="mis. 10250"
            className={inputClass(!!errors.odometer)}
          />
          {errors.odometer && (
            <p className="mt-1 text-[12px] text-red-500">{errors.odometer}</p>
          )}
        </div>

        <div>
          <HandoverPartChecklist onChange={handleChecklistChange} />
          {errors.checklist && (
            <p className="mt-1 px-6 text-[12px] text-red-500">
              {errors.checklist}
            </p>
          )}
        </div>

        <div>
          <label className="block text-[14px] text-gray-900 mb-1.5">
            Catatan
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Kondisi unit saat diserahkan."
            className={`${inputClass()} resize-y`}
          />
        </div>

        <div className="space-y-4 border-t border-gray-100 pt-4">
          <p className="text-[14px] text-gray-900">
            Tanda Tangan <span className="text-primary">*</span>
          </p>

          <SignaturePad
            label="Tanda Tangan Mitra"
            required
            value={driverSignature}
            onChange={(next) => {
              setDriverSignature(next ?? '');
              setErrors((prev) => ({ ...prev, driverSignature: undefined }));
            }}
            disabled={!dpPaid || isLoading}
            error={errors.driverSignature}
            helperText={`Ditandatangani oleh ${detail.driver?.name || 'mitra'}.`}
          />

          <SignaturePad
            label="Tanda Tangan Ops"
            required
            value={opsSignature}
            onChange={(next) => {
              setOpsSignature(next ?? '');
              setErrors((prev) => ({ ...prev, opsSignature: undefined }));
            }}
            disabled={!dpPaid || isLoading}
            error={errors.opsSignature}
          />

          <p className="text-[12px] leading-[18px] text-gray-500">
            Kedua tanda tangan diambil di perangkat ini saat serah terima (TRD
            O8), lalu BAST digenerate otomatis.
          </p>
        </div>
      </div>
    </SideBarModal>
  );
};

export default CreateHandoverModal;
